import React from "react";
import { Helmet } from "react-helmet-async";

// Per-page SEO tags. Every PUBLIC page renders one of these; the prerender
// step (scripts/prerender.js) bakes the result into that route's static HTML,
// which is what crawlers and answer engines actually read.
//
// Rules (from the SEO/AEO audit):
//  - title/description unique per route
//  - description written as a complete, liftable answer — not a teaser
//  - canonical always points at the page's own https://www.pixleyai.com URL
const ORIGIN = "https://www.pixleyai.com";

const Seo = ({
  title,
  description,
  path = "/",
  image = `${ORIGIN}/assets/og-default.png`,
  type = "website",
  jsonLd = null,
  robots = null, // e.g. "noindex" for pages that must stay out of search
}) => {
  const canonical = `${ORIGIN}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {robots && <meta name="robots" content={robots} />}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
      {/* Site-wide Organization entity (audit Phase 5) — skipped when the
          page's own jsonLd already carries it (e.g. the landing page). */}
      {!(Array.isArray(jsonLd) && jsonLd.some((d) => d && d["@type"] === "Organization")) && (
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
      )}
    </Helmet>
  );
};

// Site-wide Organization JSON-LD (audit Phase 5) — attach on the landing page.
export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Pixley",
  url: ORIGIN,
  logo: `${ORIGIN}/assets/pixley-logo-full.png`,
  sameAs: [
    "https://apps.apple.com/app/id6751121342",
  ],
};

export const SOFTWARE_APP_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Pixley",
  operatingSystem: "iOS",
  applicationCategory: "EducationalApplication",
  url: "https://apps.apple.com/app/id6751121342",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.4",
    ratingCount: "100",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free to download; membership unlocks creation.",
  },
};

export default Seo;
